import { InventoryItem, QuickSlot } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { useDraggable } from "@dnd-kit/core";
import { motion } from "motion/react";
import { useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { EquipSlot } from "@/domains/inventory/constants/equipSlot";
import { ItemRarity } from "@/domains/inventory/constants/itemRarity";
import { IS_DEV } from "@/domains/main/constants/other";
import accentColorByRarity from "@/domains/inventory/helpers/accentColorByRarity";
import useTooltip from "@/domains/inventory/hooks/useTooltip";
import InventoryBorderBox from "@/domains/inventory/components/InventoryBorderBox";
import InventoryTooltip from "@/domains/inventory/components/InventoryTooltip";

type Props = {
  equipmentId?: number;
  slot?: EquipSlot;
  quickSlot?: number;
  onUnEquip?: (equipmentId: number, slotType: string, item: InventoryItem) => Promise<void> | void;
  onUnassignQuickSlot?: (slot: QuickSlot) => Promise<void> | void;
  onRefersh?: () => Promise<void> | void;
  onContextMenu?: (e: React.MouseEvent, item: InventoryItem) => void;
  item: InventoryItem;
  disabled?: boolean;
};

export default function InventorySlotItem({
  equipmentId,
  slot,
  quickSlot,
  onUnEquip,
  onUnassignQuickSlot,
  onRefersh,
  item,
  disabled,
  onContextMenu,
}: Props) {
  const accentColor = useMemo(
    () => accentColorByRarity[item.itemTemplate.rarity] || accentColorByRarity[ItemRarity.Common],
    [item.itemTemplate.rarity],
  );

  const { itemRef, tooltipPosition, tooltipRef, handleMouseLeave, handleMouseEnter, isHovered } =
    useTooltip();

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `item-${item.itemId}-${slot || quickSlot}-${equipmentId}`,
    data: {
      ...item,
      payload: { slot, quickSlot, equipmentId },
    },
    disabled,
  });

  const onDoubleClickHandler = useCallback(async () => {
    if (slot && equipmentId) {
      await onUnEquip?.(equipmentId, slot, item);
    }
    if (quickSlot) {
      await onUnassignQuickSlot?.({
        id: quickSlot,
        slotNumber: quickSlot,
        inventoryItem: item,
      });
    }
    await onRefersh?.();
  }, [slot, equipmentId, quickSlot, onRefersh, onUnEquip, item, onUnassignQuickSlot]);

  const combinedRefs = useCallback(
    (node: HTMLElement | null) => {
      setNodeRef(node);
      itemRef.current = node;
    },
    [setNodeRef, itemRef],
  );

  return (
    <InventoryBorderBox
      ref={combinedRefs}
      className={clsx(
        "w-full h-full flex flex-col gap-1.5 items-center justify-center text-white shrink-0 overflow-clip",
        "hover:brightness-110 cursor-pointer",
        "touch-manipulation", // это очень важно
      )}
      color={accentColor}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onDoubleClick={onDoubleClickHandler}
      {...attributes}
      {...listeners}
    >
      {IS_DEV && (
        <div className="absolute bottom-1 right-1 text-10 whitespace-nowrap">
          {item.itemTemplate.name}
        </div>
      )}
      <img
        src={`https://cdn.region.game/images/inventory/items/${item.itemId}.webp`}
        alt=""
        className="max-h-[90%] max-w-[90%]"
        onContextMenu={(e) => {
          if (!disabled) {
            onContextMenu(e, item);
          }
        }}
      />
      {isHovered &&
        !isDragging &&
        createPortal(
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            ref={tooltipRef}
            className={clsx(
              "fixed z-[9999] h-fit w-fit",
              "bg-[#1A1A1A] p-3.5 rounded-[0.625rem] shadow-lg whitespace-nowrap",
              "transition-opacity duration-200",
              isHovered ? "opacity-100" : "opacity-0",
            )}
            style={{ ...tooltipPosition }}
          >
            <InventoryTooltip {...item} />
          </motion.div>,
          document.body,
        )}
    </InventoryBorderBox>
  );
}
